// RegisterModal.jsx
import React, { useEffect, useState } from "react";
import client from "../api/axiosClient";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, Send, VerifiedUser } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";



const ORGANISATION_OPTIONS = [
  "Adani Welspun Exploration Limited",
    "ASAP Fluids Private Limited",
    "Axens India (P) Limited",
    "Baker Hughes",
    "Bharat Petroleum Corporation Limited",
    "Bliss Anand Private Limited",
    "BP Exploration (Alpha) Limited",
    "Cairn Oil & Gas, Vedanta Limited",
    "Central U.P. Gas Limited",
    "Chennai Petroleum Corporation Limited",
    "Dynamic Drilling & Services Pvt. Limited",
    "Engineers India Limited",
    "Ernst & Young LLP",
    "ExxonMobil Gas (India) Pvt. Limited",
    "FMC Technologies India Private Limited",
    "GAIL (India) Limited",
    "Goa Natural Gas Private Limited",
    "GSPC LNG Limited",
    "Hindustan Petroleum Corporation Limited",
    "HPCL-Mittal Energy Limited",
    "IMC Limited",
    "Indian Gas Exchange Limited",
    "Indian Oil Corporation Limited",
    "Indian Strategic Petroleum Reserves Limited",
    "IndianOil Adani Ventures Limited",
    "Indraprastha Gas Limited",
    "Indradhanush Gas Grid Limited",
    "IRM Energy Private Limited",
    "Jindal Drilling & Industries Limited",
    "LanzaTech Private Limited",
    "Larsen & Toubro Ltd.",
    "Mangalore Refinery & Petrochemicals Limited",
    "Marine Solutionz Ship Management Private Limited",
    "Nayara Energy Limited",
    "Numaligarh Refinery Limited",
    "Oil & Natural Gas Corporation Limited",
    "Oil India Limited",
    "Petronet LNG Limited",
    "Petronet MHB Limited",
    "Pipeline Infrastructure Limited",
    "Reliance BP Mobility Limited",
    "Reliance Industries Limited",
    "S&P Global Commodity Insights",
    "Seros Energy Private Limited",
    "Shell Companies in India",
    "Siemens Limited",
    "SLB",
    "South Asia Gas Enterprise Pvt. Limited",
    "Sun Petrochemicals Private Limited",
    "THINK Gas Distribution Pvt. Ltd.",
    "Topso A/S",
    "TotalEnergies Gas & Power Projects India Pvt. Ltd.",
    "VCS Quality Services Private Limited",
    "Other",
];

const MessageCard = ({ open, severity = "info", message = "", onClose }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => setVisible(true), 10);
      const hide = setTimeout(() => {
        setVisible(false);
        setTimeout(() => onClose && onClose(), 300);
      }, 6000);
      return () => {
        clearTimeout(t);
        clearTimeout(hide);
      };
    } else {
      setVisible(false);
    }
  }, [open, onClose]);

  if (!open && !visible) return null;

  const palette = {
    success: { bg: "linear-gradient(135deg,#7ee787,#28c76f)", accent: "#0a7a3b", iconBg: "rgba(255,255,255,0.14)" },
    error: { bg: "linear-gradient(135deg,#ff9a9e,#ff6b6b)", accent: "#c40000", iconBg: "rgba(255,255,255,0.14)" },
    warning: { bg: "linear-gradient(135deg,#ffd89b,#ffb347)", accent: "#b35b00", iconBg: "rgba(255,255,255,0.14)" },
    info: { bg: "linear-gradient(135deg,#a1c4fd,#c2e9fb)", accent: "#1a73e8", iconBg: "rgba(255,255,255,0.14)" },
  }[severity] || { bg: "linear-gradient(135deg,#e0e0e0,#bdbdbd)", accent: "#333", iconBg: "rgba(255,255,255,0.14)" };

  const containerStyle = {
    position: "fixed",
    left: "50%",
    top: "24px",
    transform: `translateX(-50%) translateY(${visible ? "0" : "18px"})`,
    opacity: visible ? 1 : 0,
    transition: "transform 260ms cubic-bezier(.2,.9,.2,1), opacity 260ms ease",
    zIndex: 1400,
    minWidth: "260px",
    maxWidth: "92%",
    boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
    borderRadius: "14px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 14px",
    background: palette.bg,
    color: "#0a0a0a",
    fontFamily: "Inter, Roboto, sans-serif",
    border: `1px solid rgba(255,255,255,0.12)`,
    backdropFilter: "blur(6px)",
  };

  const iconCircle = {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: palette.iconBg,
    boxShadow: "inset 0 -4px 10px rgba(0,0,0,0.08)",
    flexShrink: 0,
  };

  const textStyle = { display: "flex", flexDirection: "column", gap: 2, lineHeight: 1.15 };

  const titleStyle = { fontSize: 13, fontWeight: 700, color: "#062b18", letterSpacing: "0.1px" };

  const messageStyle = {
    fontSize: 12,
    color: "rgba(6,43,24,0.9)",
    opacity: 0.95,
    marginTop: 2,
    maxWidth: 420,
    wordBreak: "break-word",
  };

  const closeBtnStyle = {
    marginLeft: "auto",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 6,
    borderRadius: 8,
    color: "rgba(10,10,10,0.6)",
  };

  const titleMap = { success: "Success", error: "Error", warning: "Warning", info: "Info" };

  return (
    <div role="status" aria-live="polite" style={containerStyle}>
      <div style={iconCircle}>
        <div style={{ fontSize: 18, fontWeight: 700, color: palette.accent }}>
          {severity === "success" ? "✓" : severity === "error" ? "!" : severity === "warning" ? "!" : "i"}
        </div>
      </div>

      <div style={textStyle}>
        <div style={titleStyle}>{titleMap[severity] || "Notice"}</div>
        <div style={messageStyle}>{message}</div>
      </div>

      <button
        aria-label="close message"
        onClick={() => {
          setVisible(false);
          setTimeout(() => onClose && onClose(), 260);
        }}
        style={closeBtnStyle}
      >
        <CloseIcon fontSize="small" />
      </button>
    </div>
  );
};

const RegisterModal = ({ onClose, onRegisterSuccess, open = true }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    applicant_name: "",
    applicant_phone: "",
    designation: "",
    organisation_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  // OTP related
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  // Message card state
  const [snack, setSnack] = useState({ open: false, severity: "info", message: "" });
  const [showOtherOrgInput, setShowOtherOrgInput] = useState(false);

  // password helper error message
  const [passwordHelp, setPasswordHelp] = useState("");

  // auto-fill applicant_name from first + last
  useEffect(() => {
    const fn = formData.first_name.trim();
    const ln = formData.last_name.trim();
    const combined = (fn || ln) ? `${fn} ${ln}`.trim() : "";
    setFormData((prev) => ({ ...prev, applicant_name: combined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.first_name, formData.last_name]);

  // phone input handler (digits only, max 10)
  const handlePhoneChange = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 10) {
      setFormData((p) => ({ ...p, applicant_phone: digits }));
    }
  };

  // Organization select handler
  const handleOrgSelect = (value) => {
    if (value === "Other") {
      setShowOtherOrgInput(true);
      setFormData((p) => ({ ...p, organisation_name: "" }));
    } else {
      setShowOtherOrgInput(false);
      setFormData((p) => ({ ...p, organisation_name: value }));
    }
  };

  // withoutAuth uses the axios client instance
  const withoutAuth = async (fn) => {
    const prevAuth = client.defaults.headers.common["Authorization"];
    if (prevAuth) delete client.defaults.headers.common["Authorization"];
    try {
      return await fn();
    } finally {
      if (prevAuth) client.defaults.headers.common["Authorization"] = prevAuth;
    }
  };

  // Password validation
  const validatePassword = (pw) => {
    if (!pw || pw.length < 6) {
      return { ok: false, msg: "Password must be at least 6 characters." };
    }
    if (!/\d/.test(pw)) {
      return { ok: false, msg: "Password must contain at least one number." };
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-\\[\]\/;`~+=]/.test(pw)) {
      return { ok: false, msg: "Password must contain at least one special character." };
    }
    return { ok: true };
  };

  // Update password and compute helper text live
  const handlePasswordChange = (value) => {
    setFormData((p) => ({ ...p, password: value }));
    const v = validatePassword(value);
    setPasswordHelp(v.ok ? "" : v.msg);
  };

  // show message helper
  const showMessage = ({ severity = "info", message = "" }) => {
    setSnack({ open: true, severity, message });
  };

  // Official-email enforcement
  const BLOCKED_PUBLIC_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "rediff.com", "hotmail.com"];

  const isEmailAllowedForOrg = (organisationName, email) => {
    if (!organisationName || organisationName === "Other") return true;
    if (!email || !email.includes("@")) return false;
    const domain = email.trim().split("@").pop().toLowerCase();
    return !BLOCKED_PUBLIC_DOMAINS.includes(domain);
  };

  // >>> NEW: compute whether Send OTP should be enabled
  // require email present and an organisation selected (or "Other" filled)
  const canSendOtp =
    Boolean(formData.email && formData.email.trim()) &&
    (showOtherOrgInput ? Boolean(formData.organisation_name && formData.organisation_name.trim()) : Boolean(formData.organisation_name && formData.organisation_name !== ""));

  const handleSendOtp = async () => {
    if (!formData.email) {
      return showMessage({ open: true, severity: "warning", message: "Enter email first to send OTP." });
    }

    // block public domains when org is not "Other"
    if (!isEmailAllowedForOrg(formData.organisation_name, formData.email)) {
      return showMessage({
        open: true,
        severity: "warning",
        message: "Enter Organisation official mail id  Only.",
      });
    }

    // reset verified flag and OTP input so resend becomes verifiable
    setIsOtpVerified(false);
    setOtp("");

    setOtpLoading(true);
    try {
      await withoutAuth(() => client.post("/otp/generate/", { email: formData.email }));
      setOtpSent(true);
      showMessage({ severity: "success", message: `OTP sent to ${formData.email}` });
    } catch (err) {
      // Prepare a friendly message if email is already registered
      const resp = err.response?.data;
      const respText = resp ? (typeof resp === "string" ? resp : JSON.stringify(resp)) : err.message || "";
      // detect phrases commonly used when an account exists
      const alreadyRegistered = err.response?.status === 400 && /already|exists|registered/i.test(respText);

      if (alreadyRegistered) {
        // Show the specific friendly message on Send OTP step (as requested)
        showMessage({
          severity: "warning",
          message: "This email is already registered. Please login or use 'Forgot password' to reset your password.",
        });
        // ensure OTP-related UI is not shown
        setOtpSent(false);
        setIsOtpVerified(false);
        setOtp("");
      } else {
        const msg = resp?.detail || respText || err.message;
        showMessage({ severity: "error", message: `Failed to send OTP: ${JSON.stringify(msg)}` });
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) return showMessage({ severity: "warning", message: "Enter OTP to verify." });

    if (!isEmailAllowedForOrg(formData.organisation_name, formData.email)) {
      return showMessage({
        severity: "warning",
        message: "Enter Organisation official mail id  Only.",
      });
    }

    setOtpLoading(true);
    try {
      const res = await withoutAuth(() => client.post("/otp/validate/", { email: formData.email, otp }));
      if (res.data?.valid) {
        setIsOtpVerified(true);
        showMessage({ severity: "success", message: "OTP verification successful." });
      } else {
        showMessage({ severity: "error", message: "OTP validation failed." });
      }
    } catch (err) {
      const data = err.response?.data;
      const reason = data?.reason || JSON.stringify(data) || err.message;
      showMessage({ severity: "error", message: `OTP verification failed: ${reason}` });
    } finally {
      setOtpLoading(false);
    }
  };

  // Registration submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      return showMessage({ severity: "error", message: "Passwords do not match." });
    }
    const pwCheck = validatePassword(formData.password);
    if (!pwCheck.ok) {
      return showMessage({ severity: "warning", message: `Your password is too weak: ${pwCheck.msg}` });
    }

    if (formData.applicant_phone.length !== 10) {
      return showMessage({ severity: "error", message: "Applicant phone must be 10 digits." });
    }

    if (!isEmailAllowedForOrg(formData.organisation_name, formData.email)) {
      return showMessage({
        severity: "warning",
        message: "Enter Organisation official mail id  Only.",
      });
    }

    if (!isOtpVerified) {
      return showMessage({ severity: "warning", message: "Please verify OTP before registering." });
    }

    setLoading(true);
    try {
      const regRes = await withoutAuth(() =>
        client.post("/auth/register/", {
          first_name: formData.first_name,
          last_name: formData.last_name,
          applicant_name: formData.applicant_name,
          applicant_phone: formData.applicant_phone,
          designation: formData.designation,
          organisation_name: formData.organisation_name,
          email: formData.email,
          password: formData.password,
        })
      );

      showMessage({ severity: "success", message: "Registered successfully. Attempting auto-login..." });

      // Auto-login
      try {
        const loginRes = await withoutAuth(() =>
          client.post("/auth/login/", {
            email: formData.email,
            password: formData.password,
          })
        );
        const { access, refresh } = loginRes.data || {};
        if (access) {
          localStorage.setItem("access_token", access);
          localStorage.setItem("refresh_token", refresh || "");
          client.defaults.headers.common["Authorization"] = `Bearer ${access}`;
          try {
            const payload = JSON.parse(window.atob(access.split(".")[1]));
            console.log("JWT claims:", payload);
            localStorage.setItem(
              "user_info",
              JSON.stringify({
                id: payload.user_id || payload.sub || null,
                first_name: formData.first_name,
                last_name: formData.last_name,
                organisation_name: formData.organisation_name || "",
              })
            );
          } catch (e) {
            // ignore parse errors
          }
        } else {
          console.warn("Login response did not contain access token:", loginRes.data);
        }
      } catch (loginErr) {
        console.warn("Auto-login failed:", loginErr);
        showMessage({ severity: "info", message: "Registered but auto-login failed. Please login manually." });
      }

      setLoading(false);
      onRegisterSuccess && onRegisterSuccess();
      onClose && onClose();
    } catch (err) {
      setLoading(false);
      const resp = err.response?.data;
      // Detect "email already exists" and show simple message as requested
      const respText = resp ? (typeof resp === "string" ? resp : JSON.stringify(resp)) : err.message || "";
      const alreadyRegistered =
        err.response?.status === 400 &&
        ( /already|exists|registered|user with this email/i.test(respText) ||
          (resp && (resp.email || resp.detail)) );

      if (alreadyRegistered) {
        // SHOW THIS SIMPLE MESSAGE exactly as requested
        showMessage({ severity: "error", message: `${formData.email} is already registered.` });
        return;
      }

      let msg;
      if (resp) {
        msg = typeof resp === "string" ? resp : JSON.stringify(resp);
      } else {
        msg = err.message;
      }
      showMessage({ severity: "error", message: `Registration failed: ${msg}` });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Register</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "grid", gap: 2 }}>
            <input type="hidden" name="applicant_name" value={formData.applicant_name} />

            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="First Name"
                variant="outlined"
                required
                fullWidth
                value={formData.first_name}
                onChange={(e) => setFormData((p) => ({ ...p, first_name: e.target.value }))}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                required
                fullWidth
                value={formData.last_name}
                onChange={(e) => setFormData((p) => ({ ...p, last_name: e.target.value }))}
              />
            </Box>

            <TextField
              label="Applicant Phone"
              variant="outlined"
              required
              value={formData.applicant_phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              helperText={formData.applicant_phone && formData.applicant_phone.length !== 10 ? "Phone must be exactly 10 digits." : ""}
              InputProps={{
                startAdornment: <InputAdornment position="start">+91</InputAdornment>,
              }}
            />

            <TextField
              label="Designation"
              variant="outlined"
              required
              value={formData.designation}
              onChange={(e) => setFormData((p) => ({ ...p, designation: e.target.value }))}
            />

            <FormControl fullWidth>
              <InputLabel id="org-select-label">Organisation</InputLabel>
              <Select
                labelId="org-select-label"
                value={showOtherOrgInput ? "Other" : formData.organisation_name || ""}
                label="Organisation"
                onChange={(e) => handleOrgSelect(e.target.value)}
              >
                {ORGANISATION_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {showOtherOrgInput && (
              <TextField
                label="Enter Organisation Name"
                variant="outlined"
                required
                value={formData.organisation_name}
                onChange={(e) => setFormData((p) => ({ ...p, organisation_name: e.target.value }))}
              />
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                label="Email (Official)"
                variant="outlined"
                required
                fullWidth
                value={formData.email}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, email: e.target.value }));
                  setOtpSent(false);
                  setIsOtpVerified(false);
                  setOtp("");
                }}
              />
              <Button
                variant="contained"
                endIcon={otpLoading ? <CircularProgress size={18} /> : <Send />}
                onClick={handleSendOtp}
                // <<< updated: disable unless email + organisation (or Other name) are provided
                disabled={otpLoading || !canSendOtp}
              >
                Send OTP
              </Button>
            </Box>

            {otpSent && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  label="Enter OTP"
                  variant="outlined"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  helperText="Enter the 6-digit code sent to your email"
                  inputProps={{ maxLength: 6 }}
                />
                <Button
                  variant={isOtpVerified ? "contained" : "outlined"}
                  color={isOtpVerified ? "success" : "primary"}
                  startIcon={isOtpVerified ? <VerifiedUser /> : null}
                  onClick={handleVerifyOtp}
                  disabled={isOtpVerified || otpLoading}
                >
                  {isOtpVerified ? "Verified" : "Verify OTP"}
                </Button>
                <Button onClick={handleSendOtp} disabled={otpLoading}>
                  Resend OTP
                </Button>
              </Box>
            )}

            <TextField
              label="Password"
              variant="outlined"
              required
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              helperText={passwordHelp || "Min 6 chars, include number & special char."}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              variant="outlined"
              required
              type={showPassword ? "text" : "password"}
              value={formData.confirm_password}
              onChange={(e) => setFormData((p) => ({ ...p, confirm_password: e.target.value }))}
            />

            <Typography variant="caption" color="text.secondary">
              {/* Note: Applicant Name is auto-filled from First + Last name (hidden field submitted to backend). */}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading || !isOtpVerified} startIcon={loading ? <CircularProgress size={18} /> : null}>
            {loading ? "Registering..." : "Register"}
          </Button>
        </DialogActions>
      </Dialog>

      <MessageCard open={snack.open} severity={snack.severity} message={snack.message} onClose={() => setSnack((s) => ({ ...s, open: false }))} />
    </>
  );
};

export default RegisterModal;
